import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { SphereDropzone } from "@components/common/sphere-dropzone/sphere-dropzone";
import { SphereAvatar } from "@components/header/sphere-avatar";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { isSingleFileUploadResponse } from "@custom-types/file-upload-type-guards";
import { UploadElementType } from "@custom-types/file-upload-types";
import { BaseUserProps } from "@custom-types/member-types";
import { ReactSetStateFunction } from "@custom-types/types";
import { Dialog, neutral } from "@flat-ui";
import { Typography } from "@mui/material";
import { useAppDispatch } from "@store/store-helper";
import { updateCurrentUserProfile } from "@store/user/user-slice-thunk";
import { UserProfile } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { getUserInitials } from "@utils/user-utils";
import { useState } from "react";

interface Props extends BaseUserProps {
  /** Whether the update profile picture dialog should show or not */
  isDialogOpen: boolean;

  /** Callback function to close the profile picture dialog */
  setIsDialogOpen: ReactSetStateFunction<boolean>;
}

/** Renders the update profile picture dialog and update the picture */
export function UpdateProfilePictureDialog({
  user,
  isDialogOpen,
  setIsDialogOpen,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { handleErrorWithToast } = useErrorContext();
  const { trackEvent } = useTrackEvent();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  async function updateProfilePicture(profileImageUrl: string): Promise<void> {
    try {
      trackEvent({
        name: UserProfile.updateProfile,
        props: { attribute: "profileImageUrl" },
      });
      await dispatch(
        updateCurrentUserProfile({
          content: "metaData",
          entity: {
            profileImageUrl,
            mailAddress: user.email,
            name: user.firstName,
            lastName: user.lastName ?? "",
          },
        })
      ).unwrap();
    } catch (error) {
      handleErrorWithToast({
        id: `updateProfilePicture-${Date.now().toString()}`,
        title: "Could not change profileImageUrl",
        error,
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      {/* Update profile picture dialog */}
      <Dialog
        title="Profile picture"
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      >
        <Typography
          sx={{
            fontSize: "14px",
            marginBottom: SPACE_ELEMENTS_OF_MODAL,
            color: neutral[800],
          }}
        >
          Personalize your account and help others recognize you.
        </Typography>

        <SphereDropzone
          avatar={
            <SphereAvatar
              src={user.thumbnailUrl}
              initials={getUserInitials(user)}
              size="x-large"
              shouldHideWhiteRim
            />
          }
          existingImageUrl={user.thumbnailUrl}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          context={{
            uploadElementType: UploadElementType.default,
            userId: user.identity,
          }}
          onUploadComplete={(uploadedResponse) => {
            if (isSingleFileUploadResponse(uploadedResponse)) {
              void updateProfilePicture(uploadedResponse);
            }
          }}
          onDeleteButtonClick={() => updateProfilePicture("")}
          deleteIconTooltipText="Remove profile picture"
        />
      </Dialog>
    </>
  );
}
